import Drawer from "@material-ui/core/Drawer";
import classNames from "classnames";
import { observer } from "mobx-react-lite";
import React, { FunctionComponent, ReactNode, useContext } from "react";
import { commonsStoreContext } from "../../model/CommonsStore";
import { Link } from "react-router-dom";
import { Logo, routerContext } from "../../index";
import {
  Divider,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography
} from "@material-ui/core";
import { ChevronLeft, ChevronRight } from "@material-ui/icons";
import { createStyles, makeStyles, Theme } from "@material-ui/core";
import { useTranslation } from "react-i18next";

export const drawerWidth = 265;

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: "flex"
    },
    appTitle: {
      display: "flex",
      alignItems: "center"
    },
    content: {
      backgroundColor: theme.palette.background.default,
      flexGrow: 1,
      minWidth: 0, // So the Typography noWrap works
      overflowY: "auto",
      padding: theme.spacing(3)
    },
    drawer: {
      flexShrink: 0,
      whiteSpace: "nowrap",
      width: drawerWidth
    },
    drawerOpen: {
      width: drawerWidth,
      overflowX: "hidden",
      transition: theme.transitions.create("width", {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen
      })
    },
    drawerClose: {
      overflowX: "hidden",
      transition: theme.transitions.create("width", {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen
      }),
      width: theme.spacing(14) + 1
    },
    listItems: {
      maxHeight: "calc(100% - 116px)",
      overflowY: "auto",
      overflowX: "hidden"
    },
    sideMenuCollapseItem: {
      position: "absolute",
      bottom: 0,
      backgroundColor: theme.palette.background.paper,
      width: drawerWidth - 1
    },
    toolbar: {
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-end",
      padding: "0 8px",
      ...theme.mixins.toolbar
    }
  })
);

interface SideMenuProps {
  /** Application title */
  appTitle?: string;
  /** Determines if side menu is in expended or collapsed mode */
  expanded: boolean;
  /** When provided renders header component */
  header?: ReactNode;
  /** Side menu items */
  items: ReactNode;
  landingPageLink?: string;
  /** A path to a branding logotype image */
  logoSrc?: string;
}

/**
 * Expendable side menu with optional, branding logo and application title, allows to apply home landing page link
 * */
export const SideMenu: FunctionComponent<SideMenuProps> = observer(props => {
  const classes = useStyles();
  const i18n = useTranslation();
  const {
    appTitle,
    children,
    expanded,
    landingPageLink,
    logoSrc,
    header
  } = props;
  const commons = useContext(commonsStoreContext);
  const { homeRoute } = useContext(routerContext);

  const collapseText = i18n.t("lego:collapseSideMenu", "Collapse side menu");

  const onCollapseClick = () => {
    commons.toggleSideMenuDrawerExpanded();
  };

  return (
    <div className={classes.root}>
      {header}
      <Drawer
        variant="permanent"
        open={expanded}
        className={classNames(classes.drawer, {
          [classes.drawerOpen]: expanded,
          [classes.drawerClose]: !expanded
        })}
        classes={{
          paper: classNames({
            [classes.drawerOpen]: expanded,
            [classes.drawerClose]: !expanded
          })
        }}
      >
        {header ? (
          <div className={classes.toolbar} />
        ) : (
          <span className={classes.appTitle}>
            <Link to={landingPageLink ? landingPageLink : `/${homeRoute}`}>
              <Logo
                src={logoSrc}
                hideOnMobile={false}
                image={true}
                imageStyle={{
                  marginLeft: 10,
                  marginRight: 10,
                  marginTop: 8,
                  maxWidth: 50
                }}
                color="initial"
                noWrap={true}
              />
            </Link>
            <Typography variant={"h6"}>{appTitle}</Typography>
          </span>
        )}

        <div className={classes.listItems}>{props.items}</div>

        {header ? (
          <></>
        ) : (
          <div className={classes.sideMenuCollapseItem}>
            <Divider />
            <ListItem button onClick={onCollapseClick}>
              <ListItemIcon style={{ paddingLeft: "6px" }}>
                {expanded ? <ChevronLeft /> : <ChevronRight />}
              </ListItemIcon>
              <ListItemText primary={collapseText} />
            </ListItem>
          </div>
        )}
      </Drawer>
      <main className={classes.content}>
        {header && <div className={classes.toolbar} />}
        {children}
      </main>
    </div>
  );
});
